<template>
  <section>
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div class="eyebrow">Cadastros</div>
        <h1 class="mt-2 text-3xl font-extrabold text-[#102a5a]">Tipos de entidade financeira</h1>
        <p class="mt-1 text-sm text-slate-500">Classifique as partes que participam das operações financeiras.</p>
      </div>
      <button v-if="!editing" class="btn-primary" type="button" @click="openNew"><AppIcon name="plus" class="h-4 w-4" />Incluir novo</button>
    </div>

    <section v-if="editing" class="card overflow-hidden">
      <div class="entity-form-header flex flex-col gap-3 border-b border-stone-200 bg-[#f1ece4] px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          <span class="icon-box"><AppIcon name="tags" /></span>
          <div><div class="eyebrow">Dados cadastrais</div><h2 class="mt-1 text-xl font-bold">{{ form.id ? 'Alterar tipo de entidade' : 'Novo tipo de entidade' }}</h2></div>
        </div>
        <span class="badge-amber">Campos obrigatórios *</span>
      </div>
      <form class="p-6" @submit.prevent="save">
        <div class="entity-form-grid grid gap-5 md:grid-cols-12">
          <label class="md:col-span-7"><span class="label">Nome *</span><input v-model.trim="form.name" class="input" maxlength="100" autocomplete="off" placeholder="Ex.: Garantidora"></label>
          <label class="md:col-span-3"><span class="label">Código *</span><input v-model.trim="form.code" class="input" maxlength="30" autocomplete="off" placeholder="Ex.: GAR" @input="uppercaseCode"></label>
          <label class="md:col-span-2"><span class="label">Situação *</span><select v-model="form.isActive" class="input"><option :value="true">Ativo</option><option :value="false">Inativo</option></select></label>
        </div>
        <div class="mt-6 flex flex-col-reverse gap-3 border-t border-stone-200 pt-6 sm:flex-row sm:justify-end">
          <button class="btn-secondary" type="button" :disabled="saving" @click="cancelEdit">Cancelar</button>
          <button class="btn-primary" type="submit" :disabled="saving"><AppIcon name="save" class="h-4 w-4" />{{ saving ? 'Salvando...' : 'Salvar tipo de entidade' }}</button>
        </div>
      </form>
    </section>

    <template v-else>
      <section class="card filter-panel entity-filter-panel mb-5">
        <form class="entity-filter-grid" @submit.prevent="applySearch">
          <label class="entity-search-field"><span class="label">Pesquisar registro</span><span class="search-input-wrap"><AppIcon name="search" /><input v-model.trim="search" class="input" type="search" placeholder="Nome ou código"></span></label>
          <button class="btn-primary entity-search-button" type="submit" :disabled="loading"><AppIcon name="search" class="h-4 w-4" />Buscar</button>
          <div class="record-summary" aria-live="polite"><strong>{{ totalCount }}</strong><span>registro{{ totalCount === 1 ? '' : 's' }} encontrado{{ totalCount === 1 ? '' : 's' }}</span></div>
        </form>
      </section>

      <section class="card overflow-hidden">
        <div class="flex flex-col gap-2 border-b border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div><h2 class="text-lg font-bold">Tipos cadastrados</h2><p class="text-sm text-slate-500">Clique no nome de uma coluna para ordenar os registros.</p></div>
          <span v-if="loading" class="loading-label"><span class="loading-spinner" />Atualizando</span>
        </div>

        <div class="table-scroll overflow-x-auto">
          <table class="tbl entity-table">
            <thead>
              <tr>
                <th v-for="column in columns" :key="column.value" :class="column.className">
                  <button v-if="column.sortable" type="button" class="sort-button" @click="sort(column.value)">
                    {{ column.text }}
                    <span class="sort-indicator" :class="{ active: sortBy === column.value }" aria-hidden="true">{{ sortIcon(column.value) }}</span>
                    <span class="sr-only">{{ sortDescription(column.value) }}</span>
                  </button>
                  <span v-else>{{ column.text }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading && items.length === 0"><td colspan="6" class="empty-state">Carregando registros...</td></tr>
              <tr v-else-if="items.length === 0"><td colspan="6" class="empty-state">Nenhum registro encontrado.</td></tr>
              <tr v-for="item in items" v-else :key="item.id">
                <td data-label="Id">{{ item.id }}</td>
                <td data-label="Nome"><b>{{ item.name }}</b></td>
                <td data-label="Código"><span class="code-chip">{{ item.code }}</span></td>
                <td data-label="Situação"><span :class="item.isActive ? 'badge-green' : 'badge-red'">{{ item.isActive ? 'Ativo' : 'Inativo' }}</span></td>
                <td data-label="Criado em">{{ formatDate(item.createdAt) }}</td>
                <td data-label="Ações" class="actions-cell">
                  <span class="row-actions">
                    <button class="row-action edit" type="button" title="Editar" aria-label="Editar" @click="openEdit(item.id)"><AppIcon name="pencil" /></button>
                    <button class="row-action delete" type="button" title="Excluir" aria-label="Excluir" @click="remove(item)"><AppIcon name="trash" /></button>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-footer flex flex-col gap-4 border-t border-stone-200 px-5 py-4 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between">
          <span>Exibindo {{ displayStart }} a {{ displayEnd }} de {{ totalCount }}</span>
          <div class="pagination-controls">
            <label>Linhas por página<select v-model.number="pageSize" class="pagination-select" @change="changePageSize"><option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option></select></label>
            <span>Página {{ totalPages === 0 ? 0 : page }} de {{ totalPages }}</span>
            <div class="pagination-buttons">
              <button type="button" aria-label="Página anterior" :disabled="page <= 1 || loading" @click="goToPage(page - 1)"><AppIcon name="chevronLeft" /></button>
              <button class="current-page" type="button" aria-current="page">{{ totalPages === 0 ? 0 : page }}</button>
              <button type="button" aria-label="Próxima página" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)"><AppIcon name="chevronRight" /></button>
            </div>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import Swal from 'sweetalert2'
import AppIcon from '../components/AppIcon.vue'
import service from '../services/financialEntityTypeService'

const emptyForm = () => ({ id: 0, name: '', code: '', isActive: true })

export default {
  name: 'FinancialEntityTypesView',
  components: { AppIcon },
  data: () => ({
    columns: [
      { text: 'Id', value: 'id', sortable: true },
      { text: 'Nome', value: 'name', sortable: true },
      { text: 'Código', value: 'code', sortable: true },
      { text: 'Situação', value: 'isActive', sortable: true },
      { text: 'Criado em', value: 'createdAt', sortable: true },
      { text: 'Ações', value: 'actions', sortable: false, className: 'actions-heading' }
    ],
    items: [],
    totalCount: 0,
    totalPages: 0,
    page: 1,
    pageSize: Number(process.env.VUE_APP_DEFAULT_PAGE_SIZE) || 40,
    pageSizeOptions: [20, 40, 80, 100],
    search: '',
    sortBy: 'name',
    sortDirection: 'asc',
    loading: false,
    saving: false,
    editing: false,
    form: emptyForm()
  }),
  computed: {
    displayStart () {
      return this.totalCount === 0 ? 0 : ((this.page - 1) * this.pageSize) + 1
    },
    displayEnd () {
      return Math.min(this.page * this.pageSize, this.totalCount)
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        const result = await service.getPaged({
          page: this.page,
          pageSize: this.pageSize,
          search: this.search || undefined,
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        })
        this.items = result.items
        this.totalCount = result.totalCount
        this.totalPages = result.totalPages
      } catch (error) {
        await this.showError(error)
      } finally {
        this.loading = false
      }
    },
    openNew () {
      this.form = emptyForm()
      this.editing = true
    },
    async openEdit (id) {
      this.loading = true
      try {
        const item = await service.getById(id)
        this.form = { id: item.id, name: item.name, code: item.code, isActive: item.isActive }
        this.editing = true
      } catch (error) {
        await this.showError(error)
      } finally {
        this.loading = false
      }
    },
    cancelEdit () {
      this.form = emptyForm()
      this.editing = false
    },
    uppercaseCode (event) {
      this.form.code = event.target.value.toUpperCase()
    },
    async save () {
      if (!this.form.name || !this.form.code) {
        await Swal.fire('Atenção', 'Informe o nome e o código.', 'warning')
        return
      }
      this.saving = true
      try {
        if (this.form.id) await service.update(this.form)
        else await service.add(this.form)
        await Swal.fire({ icon: 'success', title: 'Registro salvo com sucesso.', timer: 1800, showConfirmButton: false })
        this.cancelEdit()
        await this.load()
      } catch (error) {
        await this.showError(error)
      } finally {
        this.saving = false
      }
    },
    async remove (item) {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: `Excluir ${item.name}?`,
        text: 'Esta ação não poderá ser desfeita.',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#e77900',
        reverseButtons: true
      })
      if (!confirmation.isConfirmed) return
      try {
        await service.remove(item.id)
        if (this.items.length === 1 && this.page > 1) this.page--
        await Swal.fire({ icon: 'success', title: 'Registro excluído.', timer: 1500, showConfirmButton: false })
        await this.load()
      } catch (error) {
        await this.showError(error)
      }
    },
    sort (column) {
      if (this.sortBy === column) this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      else {
        this.sortBy = column
        this.sortDirection = 'asc'
      }
      this.page = 1
      this.load()
    },
    sortIcon (column) {
      if (this.sortBy !== column) return '↕'
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },
    sortDescription (column) {
      if (this.sortBy !== column) return 'Ativar ordenação por esta coluna'
      return this.sortDirection === 'asc' ? 'Ordenado de forma crescente' : 'Ordenado de forma decrescente'
    },
    applySearch () {
      this.page = 1
      this.load()
    },
    changePageSize () {
      this.page = 1
      this.load()
    },
    goToPage (page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.page = page
      this.load()
    },
    formatDate (value) {
      if (!value) return '—'
      return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)).replace(',', '')
    },
    async showError (error) {
      const validationErrors = error?.response?.data?.errors
      const firstValidation = validationErrors ? Object.values(validationErrors).flat()[0] : null
      const message = firstValidation || error?.response?.data?.title || error?.response?.data?.detail || 'Não foi possível concluir a operação.'
      await Swal.fire({ title: 'Atenção', text: message, icon: 'error', confirmButtonColor: '#e77900' })
    }
  }
}
</script>
